import React, {FC, RefObject, useState} from 'react';
import styled from 'styled-components';

import {useDebounce} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {isTeamNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {AnalyticsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {LeftPanel} from '../../../../libs/shared/ui/src/components/leftPanel/leftPanel';
import {maxLeftPanelWidth} from '../../../../libs/shared/ui/src/components/leftPanel/leftPanelHelpers';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ResizeSeparator} from '../../../components/common/resizeSeparator';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {LocalPreferencesSizesEnum} from '../../../models/api/localPreferencesModel';
import {updateSizeAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {getTeamsAccessibleForAnalytics} from '../../../store/panels/analytics/analyticsPanelSelectors';
import {getWorkspacesWidth} from '../../../store/panels/panelsSelectors';
import {AnalyticsReportsEnum} from '../../helpers/reportHelpers/reportTypes';
import {LegacyReportNavigationLinks} from './legacyReportNavigationLinks';
import {ReportNavigationAutomationReportsLinks} from './reportNavigationAutomationReportsLinks';
import {ReportNavigationExternalReports} from './reportNavigationExternalReports';
import {ReportNavigationHelpLinks} from './reportNavigationHelpLinks';
import {ReportNavigationLinks} from './reportNavigationLinks';
import {ReportNavigationRealTimeReportsLinks} from './reportNavigationRealTimeReportsLinks';
import {ReportNavigationNamespaceDropdown} from './reportNavigationTeamDropdown';

/*
 * Constants.
 */

const minReportNavigationWidth = 243;
const maxReportNavigationWidth = maxLeftPanelWidth;

/*
 * Styles.
 */

const StyledReportNavigationWrapperDiv = styled.div<{$width: number}>`
  width: ${({$width}) => $width}px;
  min-height: 0;
`;

/*
 * Props.
 */

interface ReportNavigationProps {
  panelId: AnalyticsPanelId;
  selectedNamespace: string;
  onSelectedNamespaceChange: (arg: string) => void;
  isDisabled: boolean;
  selectedReportType: AnalyticsReportsEnum;
  communicationSectionRef: RefObject<HTMLDivElement>;
}

/*
 * Component.
 */

export const ReportNavigation: FC<ReportNavigationProps> = ({
  panelId,
  selectedNamespace,
  onSelectedNamespaceChange,
  isDisabled,
  selectedReportType,
  communicationSectionRef,
}) => {
  const theme = useUserTheme();

  // Teams.
  const teams = useAppSelector(getTeamsAccessibleForAnalytics);

  // Left panel width.
  const workspacesWidth = useAppSelector(getWorkspacesWidth);
  const [reportNavigationWidth, setReportNavigationWidth] = useState(workspacesWidth);
  const analyticsReportNavigationWidth = Math.max(
    minReportNavigationWidth,
    Math.min(reportNavigationWidth, maxReportNavigationWidth),
  );

  const dispatch = useAppDispatch();
  const onReportNavigationWidthChange = (newWidth: number) =>
    dispatch(updateSizeAction(LocalPreferencesSizesEnum.WORKSPACES, newWidth));

  const onReportNavigationWidthChangeDebounce = useDebounce(onReportNavigationWidthChange, 5000);

  const onReportNavigationResize: (arg: number) => void = (newWidth) => {
    setReportNavigationWidth(newWidth);
    onReportNavigationWidthChangeDebounce(newWidth);
  };

  return (
    <>
      <StyledReportNavigationWrapperDiv $width={analyticsReportNavigationWidth}>
        <LeftPanel panelId={undefined} interactionId="analytics">
          {selectedNamespace && (
            <>
              <ReportNavigationNamespaceDropdown
                teams={teams}
                selectedNamespace={selectedNamespace}
                onSelectedNamespaceChange={onSelectedNamespaceChange}
              />
              {isTeamNamespace(selectedNamespace) && (
                <ReportNavigationRealTimeReportsLinks panelId={panelId} isDisabled={isDisabled} />
              )}
              <ReportNavigationLinks
                panelId={panelId}
                isDisabled={isDisabled}
                ref={communicationSectionRef}
              />
              <ReportNavigationAutomationReportsLinks
                panelId={panelId}
                isDisabled={isDisabled}
                namespace={selectedNamespace}
              />
              <LegacyReportNavigationLinks
                panelId={panelId}
                isDisabled={isDisabled}
                selectedReportType={selectedReportType}
              />
              <ReportNavigationExternalReports
                panelId={panelId}
                isDisabled={isDisabled}
                namespace={selectedNamespace}
              />
              <ReportNavigationHelpLinks />
            </>
          )}
        </LeftPanel>
      </StyledReportNavigationWrapperDiv>
      <ResizeSeparator
        column={2}
        size={analyticsReportNavigationWidth}
        minSize={minReportNavigationWidth}
        maxSize={maxReportNavigationWidth}
        onSizeChange={onReportNavigationResize}
        onIsResizingChange={() => onReportNavigationWidthChangeDebounce.flush()}
        color={theme.leftBar.separator}
      />
    </>
  );
};
